import { useState } from 'react'
import { colors, fonts } from './theme'
import SectionBar from './components/SectionBar'
import ProofBlock from './components/ProofBlock'

export default function MechanicsLab() {
  const [selectedTopic, setSelectedTopic] = useState('direct_lending')

  const topics = [
    { key: 'direct_lending', label: 'Direct Lending' },
    { key: 'bdc_structure', label: 'BDC Structure' },
    { key: 'clo_mechanics', label: 'CLO Mechanics' },
    { key: 'credit_analysis', label: 'Credit Analysis' },
    { key: 'distressed', label: 'Distressed & Workouts' },
    { key: 'rate_sensitivity', label: 'Rate Sensitivity' },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.subNav}>
        {topics.map(t => (
          <button
            key={t.key}
            onClick={() => setSelectedTopic(t.key)}
            style={{
              ...styles.subTab,
              ...(selectedTopic === t.key ? styles.subTabActive : {}),
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {selectedTopic === 'direct_lending' && <DirectLendingSection />}
        {selectedTopic === 'bdc_structure' && <BDCStructureSection />}
        {selectedTopic === 'clo_mechanics' && <CLOMechanicsSection />}
        {selectedTopic === 'credit_analysis' && <CreditAnalysisSection />}
        {selectedTopic === 'distressed' && <DistressedSection />}
        {selectedTopic === 'rate_sensitivity' && <RateSensitivitySection />}
      </div>
    </div>
  )
}

function DirectLendingSection() {
  return (
    <>
      <SectionBar title="Direct Lending — How It Works" subtitle="The core of private credit" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>What Is Direct Lending?</div>
        <p style={styles.conceptText}>
          Direct lending is the practice of non-bank institutions lending directly to middle-market companies
          (typically $10M-$100M EBITDA) without going through the syndicated loan market. The lender originates,
          underwrites, and holds the entire loan — no syndication, no bond market, no bank intermediary.
        </p>
        <p style={styles.conceptText}>
          This market exploded post-2008 when bank regulations (Basel III, Volcker Rule, leveraged lending
          guidelines) forced banks to pull back from middle-market lending. Private credit funds stepped into
          the gap, and the market has grown from ~$200B in 2010 to $1.7T+ today.
        </p>
      </div>

      <ProofBlock
        title="Direct Lending Economics"
        steps={[
          'Base rate: SOFR (currently ~4.3%)',
          'Credit spread: +400-600bp for first lien senior secured',
          'All-in yield: SOFR + 500bp = ~9.3% gross',
          'Origination fee: 1-2% (amortized or taken upfront)',
          'Management fee: 1.0-1.5% of AUM to the fund manager',
          'Net yield to investor: ~7-8% after fees',
          'Compare to: Investment-grade bonds at ~5.5%, high-yield bonds at ~7.5%',
        ]}
        result="Private credit offers 150-300bp premium over liquid credit, compensating for illiquidity and complexity"
      />

      <SectionBar title="Loan Structure" subtitle="Anatomy of a direct lending deal" />

      <div style={styles.structureGrid}>
        <StructureCard
          title="Security Package"
          items={[
            { label: 'Priority', value: 'First lien senior secured' },
            { label: 'Collateral', value: 'All assets + equity pledge' },
            { label: 'Guarantors', value: 'Parent + material subsidiaries' },
          ]}
        />
        <StructureCard
          title="Financial Terms"
          items={[
            { label: 'Rate', value: 'SOFR + 400-600bp (floating)' },
            { label: 'Maturity', value: '5-7 years' },
            { label: 'Amortization', value: '1-2% per year' },
            { label: 'Call protection', value: '101-102 for 1-2 years' },
          ]}
        />
        <StructureCard
          title="Covenants"
          items={[
            { label: 'Leverage', value: 'Total Debt / EBITDA < 5.0-6.0x' },
            { label: 'Coverage', value: 'FCCR > 1.0-1.2x' },
            { label: 'CapEx', value: 'Often limited by covenant' },
            { label: 'Reporting', value: 'Monthly/quarterly financials required' },
          ]}
        />
        <StructureCard
          title="Key Protections"
          items={[
            { label: 'EBITDA addbacks', value: 'Negotiated (watch for aggressive definitions)' },
            { label: 'Baskets', value: 'Restricted payments, investments, debt' },
            { label: 'Change of control', value: 'Typically triggers repayment' },
            { label: 'MFN clause', value: 'Protects against better terms on future debt' },
          ]}
        />
      </div>

      <SectionBar title="Why Companies Choose Direct Lending" subtitle="vs. bank loans or bonds" />

      <div style={styles.comparisonTable}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Factor</th>
              <th style={styles.th}>Bank Loan</th>
              <th style={styles.th}>Direct Lending</th>
              <th style={styles.th}>High-Yield Bond</th>
            </tr>
          </thead>
          <tbody>
            <CompRow factor="Speed to close" bank="6-12 weeks" direct="3-6 weeks" bond="8-16 weeks" highlight="direct" />
            <CompRow factor="Certainty" bank="Market flex risk" direct="Committed terms" bond="Market risk" highlight="direct" />
            <CompRow factor="Size" bank="$50M-$5B+" direct="$25M-$1B" bond="$200M+" />
            <CompRow factor="Cost (spread)" bank="+250-400bp" direct="+400-600bp" bond="+350-550bp" highlight="bank" />
            <CompRow factor="Covenants" bank="Mostly cov-lite" direct="Maintenance covenants" bond="Incurrence only" highlight="direct" />
            <CompRow factor="Relationship" bank="Syndicated, multiple" direct="1-3 lenders, direct" bond="Public market" highlight="direct" />
            <CompRow factor="Flexibility" bank="Standard terms" direct="Highly customizable" bond="Rigid indenture" highlight="direct" />
            <CompRow factor="Confidentiality" bank="Shared widely" direct="Private / bilateral" bond="Public filing" highlight="direct" />
          </tbody>
        </table>
      </div>
    </>
  )
}

function BDCStructureSection() {
  return (
    <>
      <SectionBar title="Business Development Companies (BDCs)" subtitle="The public vehicle for private credit" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>What Is a BDC?</div>
        <p style={styles.conceptText}>
          A BDC is a publicly-traded, closed-end investment company that lends to and invests in private
          middle-market companies. Created by Congress in 1980, BDCs were designed to channel public capital
          into businesses too small for the public debt markets. Think of them as REITs for private credit.
        </p>
      </div>

      <SectionBar title="Regulatory Framework" subtitle="What makes a BDC a BDC" />

      <div style={styles.regulatoryGrid}>
        <RegCard
          title="Investment Company Act of 1940"
          rules={[
            'Must invest at least 70% of assets in qualifying private companies',
            'Must distribute at least 90% of taxable income to shareholders (like a REIT)',
            'Regulated leverage: max 2:1 debt-to-equity (since 2018, was 1:1 before)',
            'Board of directors with majority independent members',
            'SEC reporting requirements (10-K, 10-Q, proxy)',
          ]}
        />
        <RegCard
          title="Tax Treatment"
          rules={[
            'Pass-through taxation if 90%+ income distributed',
            'No entity-level tax (avoids double taxation)',
            'Spillover income: excess income can be distributed next year',
            'Excise tax: 4% on undistributed income (incentive to distribute)',
            'Qualified dividends vs. ordinary income classification matters for investors',
          ]}
        />
      </div>

      <ProofBlock
        title="BDC Leverage Math"
        steps={[
          'Pre-2018: Max 1:1 debt/equity. $1B equity = $1B debt = $2B assets',
          'Post-2018 (SBCAA): Max 2:1 debt/equity. $1B equity = $2B debt = $3B assets',
          'Impact: 50% more assets on same equity base',
          'If portfolio yields 10% and debt costs 5%:',
          '  At 1:1 leverage: ROE = 10% + 1.0 × (10% - 5%) = 15%',
          '  At 2:1 leverage: ROE = 10% + 2.0 × (10% - 5%) = 20%',
          'But risk also amplified: a 5% loss at 2:1 leverage = 15% equity destruction',
        ]}
        result="Higher leverage boosts returns in good times but amplifies losses — the classic leverage trade-off"
      />

      <SectionBar title="Key BDC Metrics" subtitle="What to analyze" />

      <div style={styles.metricsGrid}>
        <MetricExplainer
          metric="NAV Per Share"
          formula="(Total Assets - Total Liabilities) / Shares Outstanding"
          why="The book value of the BDC. Trading above NAV = premium (market trusts management). Below NAV = discount (market skeptical). Most BDCs trade at 0.85-1.10x NAV."
        />
        <MetricExplainer
          metric="NII Per Share"
          formula="(Investment Income - Operating Expenses - Interest Expense) / Shares"
          why="The core earnings power. Must cover the dividend. If NII < dividend for extended periods, the BDC is returning capital (NAV erosion). Watch the NII coverage ratio."
        />
        <MetricExplainer
          metric="Non-Accrual Rate"
          formula="Fair Value of Non-Accruals / Total Portfolio at Fair Value"
          why="Loans where the BDC has stopped recognizing income because payment is doubtful. Industry average ~2-4%. Above 5% is a yellow flag. Above 10% is a red flag."
        />
        <MetricExplainer
          metric="Debt-to-Equity Ratio"
          formula="Total Debt / Total Net Assets"
          why="Regulatory max is 2.0x. Most BDCs operate at 1.0-1.3x. Higher leverage = higher yield but more risk. Watch the trajectory — increasing leverage in a late cycle is dangerous."
        />
      </div>
    </>
  )
}

function CLOMechanicsSection() {
  return (
    <>
      <SectionBar title="CLO Deep Mechanics" subtitle="Collateralized Loan Obligations from the inside" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>The CLO Machine</div>
        <p style={styles.conceptText}>
          A CLO is a structured credit vehicle that buys a portfolio of 150-250 leveraged loans and finances
          itself by issuing tranched debt securities. The CLO manager actively trades the loan portfolio during
          the reinvestment period. CLOs are the single most important buyer in the leveraged loan market —
          purchasing approximately 70% of all new issuance.
        </p>
      </div>

      <ProofBlock
        title="CLO Equity Return Mechanics"
        steps={[
          'CLO Portfolio: $500M in leveraged loans yielding SOFR + 400bp',
          'Gross portfolio yield: 4.3% (SOFR) + 4.0% (spread) = 8.3%',
          'Annual interest income: $500M × 8.3% = $41.5M',
          'Cost of rated tranches: ~$450M at weighted avg SOFR + 180bp = ~$29.1M',
          'CLO management fee: ~0.5% × $500M = $2.5M',
          'Trustee & admin: ~$0.5M',
          'Residual to equity: $41.5M - $29.1M - $2.5M - $0.5M = $9.4M',
          'Equity tranche size: ~$50M',
          'Equity cash yield: $9.4M / $50M = 18.8% (before defaults)',
          'After 2% default rate with 60% recovery: ~15% net return',
        ]}
        result="CLO equity targets 12-15% IRR — capturing the spread between loan yields and tranche costs"
      />

      <SectionBar title="OC & IC Tests" subtitle="The safety valves" />

      <div style={styles.testExplanation}>
        <TestCard
          title="Overcollateralization (OC) Test"
          formula="Par Value of Loans / Par Value of Tranche + All Senior Tranches"
          example="If OC test requires > 120% and ratio falls to 118%, the CLO must divert cash flows from junior tranches to pay down senior tranches until the test is cured."
          consequence="OC test failures force deleveraging — junior tranche holders and equity stop getting paid"
        />
        <TestCard
          title="Interest Coverage (IC) Test"
          formula="Interest Income from Loans / Interest Due on Tranche + All Senior Tranches"
          example="If IC test requires > 110% and ratio falls to 108%, interest payments are redirected to cure the test."
          consequence="IC test failures reduce cash available for equity distributions"
        />
        <TestCard
          title="CCC Bucket"
          formula="Typically limited to 7.5% of portfolio at par"
          example="CCC-rated loans in excess of the bucket are carried at the lower of market value or par for OC test purposes, potentially triggering OC failures."
          consequence="Downgrades to CCC can cascade into OC breaches even without actual defaults"
        />
      </div>

      <SectionBar title="CLO Lifecycle" subtitle="From warehouse to wind-down" />

      <div style={styles.lifecycleFlow}>
        <PhaseCard phase="1" title="Warehouse" duration="3-6 months" desc="Manager accumulates loans in a warehouse facility (bank credit line). Building toward target portfolio before formal CLO issuance." />
        <PhaseCard phase="2" title="Pricing / Closing" duration="1-2 weeks" desc="CLO tranches are marketed and priced. Investors commit capital. Warehouse loans are transferred into the CLO SPV." />
        <PhaseCard phase="3" title="Reinvestment Period" duration="4-5 years" desc="Manager actively trades — selling deteriorating credits, buying new loans. Principal repayments are reinvested rather than used to pay down tranches." />
        <PhaseCard phase="4" title="Amortization" duration="2-3 years" desc="New loan purchases cease. Principal repayments flow through the waterfall to pay down tranches sequentially (AAA first)." />
        <PhaseCard phase="5" title="Optional Redemption" duration="Any time after non-call" desc="Equity holders can vote to liquidate the CLO if market conditions favor refinancing or if the portfolio is substantially repaid." />
      </div>
    </>
  )
}

function CreditAnalysisSection() {
  return (
    <>
      <SectionBar title="Credit Analysis Framework" subtitle="How to evaluate a borrower — Ackman's rigor" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>The Ackman Standard</div>
        <p style={styles.conceptText}>
          William Ackman's approach to investment analysis — as chronicled in &quot;Confidence Game&quot; — demands
          understanding a business so deeply that you can construct the bear case better than the bears.
          For private credit, this means understanding every borrower as if you owned the entire company,
          not just a loan to it.
        </p>
      </div>

      <SectionBar title="The Five Pillars of Credit Analysis" />

      <div style={styles.pillarsGrid}>
        <PillarCard
          num={1}
          title="Business Quality"
          questions={[
            'What is the competitive moat? Can it widen or narrow?',
            'How cyclical is the revenue? What happened in 2008-2009, 2020?',
            'Customer concentration — what if the top customer leaves?',
            'Supplier power — can the company pass through cost increases?',
            'Regulatory risk — is the industry facing structural headwinds?',
            'Technology disruption — is the business model at risk of obsolescence?',
          ]}
        />
        <PillarCard
          num={2}
          title="Cash Flow Durability"
          questions={[
            'What is the free cash flow conversion rate? (FCF / EBITDA)',
            'How much CapEx is maintenance vs. growth? What happens if growth CapEx stops?',
            'Working capital dynamics — does growth consume or generate cash?',
            'Are there hidden cash drains? (Pension obligations, environmental liabilities, litigation)',
            'What is the cash flow under stress? (Revenue -20%, margins -300bp)',
            'Is EBITDA quality high? (Low addbacks, recurring revenue, no one-timers)',
          ]}
        />
        <PillarCard
          num={3}
          title="Capital Structure"
          questions={[
            'What is the total leverage? (Senior, total, net of cash)',
            'What is the interest coverage? (EBITDA / Cash Interest)',
            'Maturity wall — when does debt come due? Can it be refinanced?',
            'Is there adequate liquidity? (Cash + revolver availability)',
            'What is the equity cushion below the loan? (Enterprise value - senior debt)',
            'Are there structural subordination risks? (Holdco debt, unrestricted subs)',
          ]}
        />
        <PillarCard
          num={4}
          title="Recovery Analysis"
          questions={[
            'What would the company be worth in a liquidation? (Asset-based recovery)',
            'What would the company be worth as a going concern? (Multiple-based)',
            'Where is the fulcrum security? (The point where value breaks)',
            'Is the collateral package real? (Accounts receivable, inventory, IP, real estate)',
            'Jurisdictional risk — which courts would handle a restructuring?',
            'Intercreditor agreements — who has priority in a dispute?',
          ]}
        />
        <PillarCard
          num={5}
          title="Sponsor & Management"
          questions={[
            'Who is the PE sponsor? What is their track record?',
            'How much equity did the sponsor put in? (Skin in the game)',
            'Will the sponsor support the company with additional equity if needed?',
            'Management incentives — are they aligned with debt service?',
            'Has the sponsor done add-on acquisitions? (Integration risk)',
            'What is the sponsor\'s reputation in restructurings? (Cooperative vs. adversarial)',
          ]}
        />
      </div>
    </>
  )
}

function DistressedSection() {
  return (
    <>
      <SectionBar title="Distressed & Workout Mechanics" subtitle="What happens when loans go wrong" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>The Default Cascade</div>
        <p style={styles.conceptText}>
          Understanding the mechanics of default, restructuring, and recovery is essential for evaluating
          private credit risk. The process involves multiple parties with competing interests, governed by
          complex legal documents and, ultimately, the bankruptcy code.
        </p>
      </div>

      <SectionBar title="Stages of Credit Deterioration" />

      <div style={styles.stagesFlow}>
        <StageCard stage="Watch List" color={colors.amberInk} desc="Covenant headroom thinning, EBITDA declining, management turnover. Lender increases monitoring frequency. No payment issues yet." />
        <StageCard stage="Covenant Breach" color={colors.amberInk} desc="Borrower fails a financial maintenance test (e.g., leverage ratio). Lender has options: waive, amend (for a fee), or accelerate. Most breaches are waived/amended." />
        <StageCard stage="Payment Default" color={colors.redInk} desc="Borrower misses an interest or principal payment. Grace period typically 5-30 days. This is material — triggers cross-defaults on other debt, potentially accelerates all obligations." />
        <StageCard stage="Forbearance" color={colors.redInk} desc="Lender agrees not to exercise remedies for a period while parties negotiate. Borrower typically pays a forbearance fee and agrees to tighter terms." />
        <StageCard stage="Restructuring" color={colors.redInk} desc="Out-of-court: Amend-and-extend, debt-for-equity swap, new money injection. In-court: Chapter 11 bankruptcy — automatic stay, DIP financing, plan of reorganization." />
        <StageCard stage="Recovery" color={colors.greenInk} desc="Lender receives some combination of: cash paydown, new debt at lower face value, equity in reorganized company. Historical recovery for first lien: 60-70%. Second lien: 30-40%." />
      </div>

      <ProofBlock
        title="Recovery Math — First Lien Loan"
        steps={[
          'Company Enterprise Value at restructuring: $100M',
          'First Lien Debt: $80M (face value)',
          'Second Lien Debt: $30M (face value)',
          'Unsecured Claims: $20M',
          'Total Claims: $130M vs. $100M enterprise value',
          'First Lien recovery: $80M / $80M = 100% (fully covered)',
          'Remaining value: $100M - $80M = $20M',
          'Second Lien recovery: $20M / $30M = 67%',
          'Unsecured recovery: $0M / $20M = 0%',
        ]}
        result="The fulcrum security is second lien — it's the class that gets partial recovery and typically converts to equity"
      />
    </>
  )
}

function RateSensitivitySection() {
  return (
    <>
      <SectionBar title="Interest Rate Sensitivity" subtitle="How rates flow through private credit" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>Floating Rate = Double-Edged Sword</div>
        <p style={styles.conceptText}>
          Nearly all private credit loans are floating rate (SOFR + spread). This means private credit investors
          benefit directly from rising rates — unlike fixed-rate bond investors who see price declines.
          But the borrowers bear the rate risk, and higher rates mean higher debt service costs, which can
          push leveraged companies toward distress.
        </p>
      </div>

      <ProofBlock
        title="Rate Impact on Borrower"
        steps={[
          'Company: $50M EBITDA, 5x leverage = $250M debt',
          'Rate: SOFR + 450bp',
          'Scenario 1 (SOFR = 2%): Interest = $250M × 6.5% = $16.25M. Coverage = 3.1x',
          'Scenario 2 (SOFR = 4%): Interest = $250M × 8.5% = $21.25M. Coverage = 2.4x',
          'Scenario 3 (SOFR = 5.5%): Interest = $250M × 10% = $25M. Coverage = 2.0x',
          'Cash flow to equity: $50M EBITDA - $25M interest - $5M CapEx - $5M tax = $15M',
          'At SOFR 2%: $23.75M cash to equity',
          'At SOFR 5.5%: $15M cash to equity (37% reduction)',
        ]}
        result="A 350bp rate increase reduces borrower cash flow by ~37% — this is where defaults come from"
      />

      <SectionBar title="Rate Sensitivity by Entity" subtitle="Winners and losers" />

      <div style={styles.sensitivityGrid}>
        <SensitivityCard
          entity="Direct Lender / BDC"
          impact="positive"
          detail="Higher SOFR = higher interest income. Most BDC liabilities are also floating, but with lower spreads, so net interest margin expands. Biggest winner in rising rate environment — until defaults spike."
        />
        <SensitivityCard
          entity="Borrower"
          impact="negative"
          detail="Higher SOFR = higher debt service. Interest coverage deteriorates. Cash available for growth, CapEx, and equity distributions shrinks. The most leveraged companies face covenant breaches or payment defaults."
        />
        <SensitivityCard
          entity="CLO Equity"
          impact="positive"
          detail="Floating-rate assets (loans) and floating-rate liabilities (CLO tranches). Net spread to equity widens slightly with rates because CLO tranche spreads are fixed at issuance while loan spreads reset. But default risk increases."
        />
        <SensitivityCard
          entity="CLO AAA Holder"
          impact="neutral"
          detail="Floating rate = no duration risk. Subordination provides credit protection. Rising rates don't directly hurt AAA holders, but could trigger OC test failures if borrower distress increases defaults."
        />
      </div>
    </>
  )
}

// Shared sub-components
function StructureCard({ title, items }) {
  return (
    <div style={styles.structureCard}>
      <div style={styles.structureTitle}>{title}</div>
      {items.map((item, i) => (
        <div key={i} style={styles.structureRow}>
          <span style={styles.structureLabel}>{item.label}</span>
          <span style={styles.structureValue}>{item.value}</span>
        </div>
      ))}
    </div>
  )
}

function CompRow({ factor, bank, direct, bond, highlight }) {
  return (
    <tr style={styles.tr}>
      <td style={{ ...styles.td, fontWeight: 600, color: colors.textPrimary }}>{factor}</td>
      <td style={{ ...styles.td, color: highlight === 'bank' ? colors.greenInk : colors.textSecondary }}>{bank}</td>
      <td style={{ ...styles.td, color: highlight === 'direct' ? colors.greenInk : colors.textSecondary }}>{direct}</td>
      <td style={{ ...styles.td, color: highlight === 'bond' ? colors.greenInk : colors.textSecondary }}>{bond}</td>
    </tr>
  )
}

function RegCard({ title, rules }) {
  return (
    <div style={styles.regCard}>
      <div style={styles.regTitle}>{title}</div>
      {rules.map((r, i) => (
        <div key={i} style={styles.regItem}>{r}</div>
      ))}
    </div>
  )
}

function MetricExplainer({ metric, formula, why }) {
  return (
    <div style={styles.metricExplainer}>
      <div style={styles.metricName}>{metric}</div>
      <div style={styles.metricFormula}>{formula}</div>
      <div style={styles.metricWhy}>{why}</div>
    </div>
  )
}

function TestCard({ title, formula, example, consequence }) {
  return (
    <div style={styles.testCard}>
      <div style={styles.testTitle}>{title}</div>
      <div style={styles.testFormula}>{formula}</div>
      <div style={styles.testExample}>{example}</div>
      <div style={styles.testConsequence}>{consequence}</div>
    </div>
  )
}

function PhaseCard({ phase, title, duration, desc }) {
  return (
    <div style={styles.phaseCard}>
      <div style={styles.phaseNum}>{phase}</div>
      <div style={styles.phaseContent}>
        <div style={styles.phaseTitle}>{title} <span style={styles.phaseDuration}>({duration})</span></div>
        <div style={styles.phaseDesc}>{desc}</div>
      </div>
    </div>
  )
}

function PillarCard({ num, title, questions }) {
  return (
    <div style={styles.pillarCard}>
      <div style={styles.pillarHeader}>
        <span style={styles.pillarNum}>{num}</span>
        <span style={styles.pillarTitle}>{title}</span>
      </div>
      {questions.map((q, i) => (
        <div key={i} style={styles.pillarQuestion}>{q}</div>
      ))}
    </div>
  )
}

function StageCard({ stage, color, desc }) {
  return (
    <div style={{ ...styles.stageCard, borderLeftColor: color }}>
      <div style={{ ...styles.stageName, color }}>{stage}</div>
      <div style={styles.stageDesc}>{desc}</div>
    </div>
  )
}

function SensitivityCard({ entity, impact, detail }) {
  const impactColor = impact === 'positive' ? colors.greenInk : impact === 'negative' ? colors.redInk : colors.amberInk
  const impactLabel = impact === 'positive' ? 'BENEFITS' : impact === 'negative' ? 'HURT' : 'NEUTRAL'

  return (
    <div style={styles.sensCard}>
      <div style={styles.sensHeader}>
        <span style={styles.sensEntity}>{entity}</span>
        <span style={{ ...styles.sensImpact, color: impactColor }}>{impactLabel}</span>
      </div>
      <div style={styles.sensDetail}>{detail}</div>
    </div>
  )
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  subNav: {
    display: 'flex',
    gap: 2,
    padding: '8px 32px',
    borderBottom: `1px solid ${colors.rule}`,
    background: colors.navyDeep,
    flexWrap: 'wrap',
  },
  subTab: {
    padding: '4px 14px',
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 500,
    color: colors.textMuted,
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  subTabActive: {
    color: colors.gold,
    borderColor: colors.goldBorder,
    background: colors.goldBg,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  // Concept blocks
  conceptBlock: {
    padding: 20,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  conceptTitle: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 10,
  },
  conceptText: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.7,
    marginBottom: 8,
  },
  // Structure grid
  structureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 12,
  },
  structureCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  structureTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.rule}`,
  },
  structureRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '5px 0',
    borderBottom: `1px solid ${colors.ruleLight}`,
  },
  structureLabel: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textMuted,
  },
  structureValue: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textPrimary,
    textAlign: 'right',
    maxWidth: '60%',
  },
  // Table
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: fonts.sans,
    fontSize: 13,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  th: {
    padding: '10px 16px',
    textAlign: 'left',
    fontFamily: fonts.serif,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: colors.textMuted,
    background: colors.navyDeep,
    borderBottom: `1px solid ${colors.rule}`,
  },
  tr: {
    borderBottom: `1px solid ${colors.ruleLight}`,
  },
  td: {
    padding: '8px 16px',
    color: colors.textSecondary,
    fontSize: 12,
  },
  comparisonTable: {
    overflow: 'auto',
  },
  // Regulatory
  regulatoryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  regCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  regTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 12,
  },
  regItem: {
    padding: '5px 0',
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.5,
    borderBottom: `1px solid ${colors.ruleLight}`,
  },
  // Metric explainers
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
  },
  metricExplainer: {
    padding: 16,
    background: colors.navyDeep,
    border: `1px solid ${colors.rule}`,
    borderLeft: `3px solid ${colors.gold}`,
    borderRadius: 3,
  },
  metricName: {
    fontFamily: fonts.serif,
    fontSize: 15,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 6,
  },
  metricFormula: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.steel,
    padding: '4px 8px',
    background: colors.steelBg,
    borderRadius: 2,
    marginBottom: 10,
    display: 'inline-block',
  },
  metricWhy: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  // Test cards
  testExplanation: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  testCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  testTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 6,
  },
  testFormula: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.gold,
    padding: '4px 8px',
    background: colors.goldBg,
    borderRadius: 2,
    marginBottom: 10,
    display: 'inline-block',
  },
  testExample: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
    marginBottom: 8,
  },
  testConsequence: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.redInk,
    padding: '6px 10px',
    background: colors.redBg,
    borderRadius: 2,
  },
  // Phase cards
  lifecycleFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  phaseCard: {
    display: 'flex',
    gap: 16,
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  phaseNum: {
    fontFamily: fonts.mono,
    fontSize: 20,
    fontWeight: 700,
    color: colors.gold,
    minWidth: 32,
    textAlign: 'center',
  },
  phaseContent: { flex: 1 },
  phaseTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 4,
  },
  phaseDuration: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
  },
  phaseDesc: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  // Pillar cards
  pillarsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
  },
  pillarCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  pillarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.rule}`,
  },
  pillarNum: {
    fontFamily: fonts.mono,
    fontSize: 18,
    fontWeight: 700,
    color: colors.gold,
    minWidth: 28,
  },
  pillarTitle: {
    fontFamily: fonts.serif,
    fontSize: 15,
    fontWeight: 600,
    color: colors.steel,
  },
  pillarQuestion: {
    padding: '5px 0',
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.5,
    borderBottom: `1px solid ${colors.ruleLight}`,
  },
  // Stages
  stagesFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  stageCard: {
    padding: '12px 16px',
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderLeft: '3px solid',
    borderRadius: 3,
  },
  stageName: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
  },
  stageDesc: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  // Sensitivity
  sensitivityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12,
  },
  sensCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  sensHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  sensEntity: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  sensImpact: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 2,
  },
  sensDetail: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
}
